/**
 * SeoHead · v2.7.0 batch CT · Task 4
 * ==================================
 * Lightweight zero-dependency replacement for `react-helmet`. Mutates the
 * <head> imperatively on mount + restores on unmount so each marketing page
 * can declare its own title / description / canonical / OG metadata without
 * adding a new runtime dep.
 *
 * Regional keywords are woven into every prop's default fallback so even a
 * page that forgets to pass `keywords` still ranks for Weatherford OK and
 * Clinton OK searches.
 */
import { useEffect } from "react";

const DEFAULT_KEYWORDS = [
  "Taxi Weatherford Oklahoma",
  "Taxi Weatherford OK",
  "taxi weatherford ok",
  "Taxi Clinton OK",
  "taxi Clinton Oklahoma",
  "Weatherford Oklahoma taxi service",
  "Clinton Oklahoma taxi",
  "24/7 taxi Oklahoma",
  "Western Oklahoma taxi",
  "Buller Taxi Services, LLC",
].join(", ");

const SITE_URL = "https://bullertaxi.com";
const DEFAULT_OG_IMAGE = `${SITE_URL}/buller-taxi-app-icon-1024.png`;

function upsertMeta({ name, property, content }) {
  if (!content) return null;
  const selector = name
    ? `meta[name="${name}"]`
    : `meta[property="${property}"]`;
  let el = document.head.querySelector(selector);
  let created = false;
  if (!el) {
    el = document.createElement("meta");
    if (name) el.setAttribute("name", name);
    if (property) el.setAttribute("property", property);
    document.head.appendChild(el);
    created = true;
  }
  const prev = el.getAttribute("content");
  el.setAttribute("content", content);
  return { el, prev, created };
}

function upsertLink({ rel, href, hreflang }) {
  if (!href) return null;
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]`;
  let el = document.head.querySelector(selector);
  let created = false;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
    created = true;
  }
  const prev = el.getAttribute("href");
  el.setAttribute("href", href);
  return { el, prev, created };
}

/**
 * @param {object} props
 * @param {string} props.title         · full <title> (will append site suffix)
 * @param {string} props.description   · meta description (155–160 chars ideal)
 * @param {string} [props.keywords]    · optional comma-separated keyword list
 * @param {string} [props.canonical]   · relative or absolute URL
 * @param {string} [props.image]       · absolute Open Graph image URL
 * @param {string} [props.type]        · og:type — defaults to "website"
 * @param {Array<{lang:string, href:string}>} [props.hreflangs] · regional
 *        alternate-language tags. Include an entry with lang="x-default"
 *        to signal Google's fallback for unmatched locales.
 */
export default function SeoHead({
  title,
  description,
  keywords,
  canonical,
  image,
  type = "website",
  hreflangs,
}) {
  useEffect(() => {
    const fullTitle = title
      ? `${title} · Buller Taxi Services, LLC · Taxi Weatherford OK & Taxi Clinton OK`
      : "Taxi Weatherford, Oklahoma · Taxi Clinton, OK · Buller Taxi Services, LLC";
    const prevTitle = document.title;
    document.title = fullTitle;

    const canonicalHref = canonical
      ? (canonical.startsWith("http") ? canonical : `${SITE_URL}${canonical}`)
      : SITE_URL + (window.location.pathname || "/");

    const writes = [
      upsertMeta({ name: "description", content: description }),
      upsertMeta({ name: "keywords", content: keywords || DEFAULT_KEYWORDS }),
      upsertMeta({ property: "og:title", content: fullTitle }),
      upsertMeta({ property: "og:description", content: description }),
      upsertMeta({ property: "og:url", content: canonicalHref }),
      upsertMeta({ property: "og:image", content: image || DEFAULT_OG_IMAGE }),
      upsertMeta({ property: "og:type", content: type }),
      upsertMeta({ name: "twitter:title", content: fullTitle }),
      upsertMeta({ name: "twitter:description", content: description }),
      upsertMeta({ name: "twitter:image", content: image || DEFAULT_OG_IMAGE }),
      upsertLink({ rel: "canonical", href: canonicalHref }),
      // v2.7.0 batch CV · hreflang regional alternates — signals to Google
      // that Weatherford / Clinton landing pages are regional variants of
      // the same English service so the local-pack ranking pools share
      // authority instead of competing against each other.
      ...((hreflangs || []).map((h) =>
        upsertLink({ rel: "alternate", hreflang: h.lang, href: h.href })
      )),
    ].filter(Boolean);

    return () => {
      document.title = prevTitle;
      writes.forEach((w) => {
        if (!w || !w.el) return;
        if (w.created) {
          if (w.el.parentNode) w.el.parentNode.removeChild(w.el);
        } else if (w.prev !== null && w.prev !== undefined) {
          if (w.el.tagName === "LINK") w.el.setAttribute("href", w.prev);
          else w.el.setAttribute("content", w.prev);
        }
      });
    };
  }, [title, description, keywords, canonical, image, type, hreflangs]);

  return null;
}

export { DEFAULT_KEYWORDS, SITE_URL, DEFAULT_OG_IMAGE };
